// What may be labelled "Baseline" — and what may not.
//
// measure_state.baseline_value is WF9's ROLLING mean of the last 3 BP readings in 15 days; CMS
// receives the mean of the HIGHEST 3 systolic in 15 days (WF37, S-96). They are different
// statistics, so the stored number can disagree with the filing — live: stored 120 vs filed
// 138/81 on the same submission row, and a care coordinator read 120/79 off this app and typed it
// into a Care Update that was faxed to the patient's PCP.
//
// So: a number is displayable only if the FILING records it. The one exception is a LAB row
// WF37 stamped after CMS accepted the baseline (`labWriteBack`): nothing else writes a lab's
// baseline, and the filing record omits a lab nobody typed. BP, weight and BMI stay filing-only.
// Specs: docs/specs/2026-08-25-baseline-is-what-cms-received.md,
// docs/specs/2026-10-14-lab-baseline-written-back.md.
//
// ⚠️ `comparisonBaseline` is deliberately separate — a chart band is not a baseline. Reserved for
// the companion's weight card (Phase 2); no back-office consumer yet.

export const BASELINE_NOT_SUBMITTED = 'Baseline not submitted';
// Accepted, but the filing does not say what it sent — "not submitted" would be false here.
export const BASELINE_NOT_RECORDED = 'Baseline not recorded';
// Submissions not loaded, or the fetch failed. Neither claim above is knowable yet.
export const BASELINE_UNKNOWN = '—';

// The baseline-only BP selection rules (S-96: quarterly/final use the latest-day rule). Rows predating
// request_payload.report_kind are identified by them; the same-day rule replaced the first on 2026-10-30.
const BASELINE_SELECTION_RULE = 'highest_3_systolic_in_15d';
const BASELINE_DAY_SELECTION_RULE = 'highest_day_3_systolic_in_15d';
const ACCEPTED = new Set(['success', 'duplicate']);

export type FilingTrack = 'eckm' | 'ckm';
// The lab half of WF37's TRACK_MEASURES (Payment Amounts Tables 3/4): eCKM files no kidney labs.
// S-120 #9 compares it to WF37.
const TRACK_FILED_LABS: Record<FilingTrack, readonly string[]> = {
  eckm: ['hba1c', 'ldl_c'],
  ckm: ['hba1c', 'ldl_c', 'egfr', 'uacr'],
};
const TRACK_LABEL: Record<FilingTrack, string> = { eckm: 'eCKM', ckm: 'CKM' };
// The fourth fact beside the three above: a lab the track never files.
export const baselineNotInFilingCopy = (track: FilingTrack): string => `Not part of the ${TRACK_LABEL[track]} filing`;
// The only measures `labWriteBack` may read: WF9 rolls the stored BP/weight/BMI baseline.
const WRITTEN_BACK_LABS: readonly string[] = ['hba1c', 'ldl_c', 'egfr', 'uacr'];
// Before WF37's single-writer fixes reached PROD (S-120 #10/#14), a rejected or typed-but-unsent
// lab could be stamped. Every filing that relies on the write-back is on or after this date.
export const LAB_WRITEBACK_SINCE = '2026-09-15T00:00:00Z';

// WF37 stamps 'reportable' on an accepted baseline (S-120); S-30 stamps both BP rows, so bp_systolic
// alone answers "has CMS accepted a baseline for this period". On a BP row baseline_valid_flag is
// WF9's readiness value, not the filing. View vs CRUD spellings: measure_status/status,
// care_period_id/id_care_period.
export interface MeasureStateRow {
  measure_type?: string | null;
  status?: string | null;
  measure_status?: string | null;
  baseline_value?: unknown;
  baseline_valid_flag?: boolean | null;
  care_period_id?: string | number | null;
  id_care_period?: string | number | null;
}
const statusOf = (m: MeasureStateRow): string | null | undefined => m.measure_status ?? m.status;
export function isBaselineFiled(measureStates: MeasureStateRow[] | null | undefined): boolean {
  return (measureStates || []).some((m) => !!m && m.measure_type === 'bp_systolic' && statusOf(m) === 'reportable');
}

export interface BaselineFilingRow {
  operation?: string | null;
  status?: string | null;
  result_code?: string | null;
  report_kind?: string | null;
  created?: string | null;
  created_at?: string | null;
  withings_user_id?: string | number | null;
  id_care_period?: string | number | null;
  request_payload?: unknown;
  [key: string]: unknown;
}

function payloadOf(row: BaselineFilingRow): Record<string, unknown> {
  const raw = row?.request_payload;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) as Record<string, unknown>; } catch { return {}; }
  }
  return (raw && typeof raw === 'object' && !Array.isArray(raw)) ? raw as Record<string, unknown> : {};
}

// bp_avg_sent/bp_selection_rule live under request_payload.bp_audit on newer rows and at the top
// level on older ones — read both.
function auditOf(payload: Record<string, unknown>): Record<string, unknown> {
  const audit = payload.bp_audit;
  return (audit && typeof audit === 'object' && !Array.isArray(audit))
    ? { ...payload, ...audit as Record<string, unknown> }
    : payload;
}

const idOrNull = (v: unknown): string | null => (v == null || v === '' ? null : String(v));

interface Filing { row: BaselineFilingRow; payload: Record<string, unknown> }

// Accepted baseline filings for this patient, oldest first. Rejected filings, quarterly filings and
// other patients' rows are all excluded — the same predicate WF70 applies.
function acceptedBaselineFilings(
  submissions: readonly BaselineFilingRow[] | null | undefined,
  withingsUserId?: string | number | null,
): Filing[] {
  const rows = Array.isArray(submissions) ? submissions : [];
  return rows
    .filter((r) => (withingsUserId == null
      || String(r?.withings_user_id ?? '') === String(withingsUserId)))
    .filter((r) => String(r?.operation ?? '').startsWith('report'))
    .filter((r) => ACCEPTED.has(String(r?.status ?? '')) || ACCEPTED.has(String(r?.result_code ?? '')))
    .map((r) => ({ row: r, payload: auditOf(payloadOf(r)) }))
    .filter(({ row, payload }) => {
      const kind = String(row?.report_kind ?? payload.report_kind ?? '').toLowerCase();
      if (kind) return kind === 'baseline';
      const rule = String(payload.bp_selection_rule ?? '');
      return rule === BASELINE_SELECTION_RULE || rule === BASELINE_DAY_SELECTION_RULE;
    })
    .sort((a, b) => String(a.row.created ?? a.row.created_at ?? '')
      .localeCompare(String(b.row.created ?? b.row.created_at ?? '')));
}

/** The accepted CMS *baseline* filing for this patient (the newest), or null. */
export function findBaselineFiling(
  submissions: readonly BaselineFilingRow[] | null | undefined,
  withingsUserId?: string | number | null,
): { payload: Record<string, unknown>; at: string | null } | null {
  const last = acceptedBaselineFilings(submissions, withingsUserId).at(-1);
  return last ? { payload: last.payload, at: last.row.created ?? last.row.created_at ?? null } : null;
}

const numOrNull = (v: unknown): number | null => {
  const n = Number(v);
  // A zero baseline is a seeded placeholder, never a clinical value.
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** The BP baseline CMS received, or null when nothing has been filed. */
export function filedBpBaseline(
  submissions: readonly BaselineFilingRow[] | null | undefined,
  withingsUserId?: string | number | null,
): { sys: number; dia: number | null } | null {
  const filing = findBaselineFiling(submissions, withingsUserId);
  const avg = filing?.payload?.bp_avg_sent;
  if (!avg || typeof avg !== 'object') return null;
  const sys = numOrNull((avg as Record<string, unknown>).sys);
  if (sys == null) return null;
  return { sys, dia: numOrNull((avg as Record<string, unknown>).dia) };
}

// WF37's slug vocabulary differs from measure_type for LDL.
const FILED_SLUG: Record<string, string> = { ldl_c: 'ldl' };

// The audit key each measure's filed value rides on. BP has its own (`filedBpBaseline`).
const AUDIT_KEY: Record<string, string> = { weight: 'weight_sent', bmi: 'bmi_sent' };

/** False when the track's filing never carries this lab (eCKM eGFR/uACR); true when unknown. */
export function trackFilesMeasure(track: FilingTrack | null | undefined, measureType: string): boolean {
  if (!track || !Object.hasOwn(TRACK_FILED_LABS, track)) return true;
  if (!TRACK_FILED_LABS.ckm.includes(measureType)) return true;
  return TRACK_FILED_LABS[track].includes(measureType);
}

// The lab baseline WF37 wrote back from what the bundle sent (S-120 #14). Only an accepted baseline
// verdict writes a lab row's baseline_value + 'reportable' (WF72 never does, S-129), so the row is
// the filing — provided it is this filing's period and newer than the fixes that made that true.
function labWriteBack(
  measureType: string,
  measureStates: readonly MeasureStateRow[] | null | undefined,
  filings: Filing[],
): number | null {
  if (!WRITTEN_BACK_LABS.includes(measureType)) return null;
  const cp = idOrNull(filings.at(-1)?.row.id_care_period);
  if (!cp) return null;
  const since = Date.parse(LAB_WRITEBACK_SINCE);
  const inPeriod = filings.filter((f) => idOrNull(f.row.id_care_period) === cp);
  if (!inPeriod.every((f) => Date.parse(String(f.row.created ?? f.row.created_at ?? '')) >= since)) return null;
  const row = (measureStates || []).find((m) => !!m && m.measure_type === measureType
    && idOrNull(m.care_period_id ?? m.id_care_period) === cp);
  if (!row || statusOf(row) !== 'reportable' || row.baseline_valid_flag !== true) return null;
  return numOrNull(row.baseline_value);
}

export interface FiledBaselineOptions {
  /** measure_state rows (view or CRUD) — read for the four labs only, see `labWriteBack`. */
  measureStates?: readonly MeasureStateRow[] | null;
  /** A lab the track never files has no baseline, whatever was typed. */
  track?: FilingTrack | null;
}

// A typed echo, else the audit key, else (labs) WF37's write-back. Never the stored BP/weight/BMI
// column — WF9 rolls it, so it is a live value wearing a historical label.
export function filedMeasureBaseline(
  measureType: string,
  submissions: readonly BaselineFilingRow[] | null | undefined,
  withingsUserId?: string | number | null,
  options: FiledBaselineOptions | null = null,
): number | null {
  const { measureStates = null, track = null } = options ?? {};
  if (!trackFilesMeasure(track, measureType)) return null;
  const filings = acceptedBaselineFilings(submissions, withingsUserId);
  const filing = filings.at(-1);
  if (!filing) return null;
  const entries = filing.payload?.manual_entries;
  if (Array.isArray(entries)) {
    const wanted = FILED_SLUG[measureType] ?? measureType;
    const hit = entries.find((e) => e && String((e as Record<string, unknown>).slug) === wanted);
    const typed = hit ? numOrNull((hit as Record<string, unknown>).value) : null;
    if (typed != null) return typed;
  }
  const auditKey = AUDIT_KEY[measureType];
  if (auditKey) return numOrNull(filing.payload[auditKey]);
  return labWriteBack(measureType, measureStates, filings);
}

// Four distinct facts: only "not submitted" tells a coordinator the milestone is outstanding, so
// an unloaded/failed fetch must not collapse into it, and a lab the track never files is neither.
export function baselineMissingCopy(
  submissions: readonly BaselineFilingRow[] | null | undefined,
  withingsUserId?: string | number | null,
  options: { measureType?: string | null; track?: FilingTrack | null } | null = null,
): string {
  const { measureType = null, track = null } = options ?? {};
  if (measureType && track && !trackFilesMeasure(track, measureType)) return baselineNotInFilingCopy(track);
  if (submissions == null) return BASELINE_UNKNOWN;
  return findBaselineFiling(submissions, withingsUserId)
    ? BASELINE_NOT_RECORDED
    : BASELINE_NOT_SUBMITTED;
}

/**
 * A reference point for trend bands and progress bars — NOT a baseline to display. Prefers the
 * filed value when there is one, else the platform's readiness value, else the caller's fallback
 * (typically the first reading in the window).
 */
export function comparisonBaseline(
  filed: number | null,
  storedValue: unknown,
  fallback?: number | null,
): number | null {
  return filed ?? numOrNull(storedValue) ?? (fallback ?? null);
}
